import { useState, useEffect } from 'react';
import { X, Eye, EyeOff, Copy, CheckCircle } from 'lucide-react';
import { jwtDecode } from 'jwt-decode';
import { useUpdateTokenCodes } from '../../hooks/useTokens';
import { normalizeCodeInput } from '../../utils/slugify';
import type { ApiError, Token, TokenDetails, UpdateTokenCodesRequest } from '../../types';

interface TokenViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  tokenDetails: TokenDetails | null;
  token?: Token;
  isLoading: boolean;
}

export const TokenViewModal = ({ isOpen, onClose, tokenDetails, token, isLoading }: TokenViewModalProps) => {
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [showRefreshToken, setShowRefreshToken] = useState(false);
  const [showDecodedJWT, setShowDecodedJWT] = useState(false);
  const [decodedAccessToken, setDecodedAccessToken] = useState<any>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isEditingCodes, setIsEditingCodes] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeValues, setCodeValues] = useState<UpdateTokenCodesRequest>({
    tokenCode: '',
    providerCode: '',
    templateCode: '',
  });
  const updateTokenCodes = useUpdateTokenCodes(token?.id);

  // Decode JWT when access token is available
  useEffect(() => {
    if (tokenDetails?.accessToken) {
      try {
        const decoded = jwtDecode(tokenDetails.accessToken);
        setDecodedAccessToken(decoded);
      } catch (error) {
        console.error('Failed to decode JWT:', error);
        setDecodedAccessToken(null);
      }
    }
  }, [tokenDetails?.accessToken]);

  useEffect(() => {
    if (!isOpen) {
      setIsEditingCodes(false);
      setCodeError(null);
    }
  }, [isOpen]);

  const maskToken = (token: string | undefined) => {
    if (!token) return 'N/A';
    if (token.length <= 20) return '••••••••••••••••••••';
    return `${token.substring(0, 8)}••••••••••••${token.substring(token.length - 8)}`;
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const displayScope = tokenDetails?.scope?.trim() ? tokenDetails.scope : 'Not set';

  const getInitialCodeValues = (): UpdateTokenCodesRequest => ({
    tokenCode: token?.tokenCode ?? '',
    providerCode: token?.providerCode ?? '',
    templateCode: token?.providerTemplateCode ?? '',
  });

  const normalizeDraftValues = (values: UpdateTokenCodesRequest): UpdateTokenCodesRequest => ({
    tokenCode: normalizeCodeInput(values.tokenCode),
    providerCode: normalizeCodeInput(values.providerCode),
    templateCode: normalizeCodeInput(values.templateCode),
  });
  const normalizedInitialCodeValues = normalizeDraftValues(getInitialCodeValues());
  const normalizedCurrentCodeValues = normalizeDraftValues(codeValues);
  const hasCodeChanges =
    normalizedCurrentCodeValues.tokenCode !== normalizedInitialCodeValues.tokenCode ||
    normalizedCurrentCodeValues.providerCode !== normalizedInitialCodeValues.providerCode ||
    normalizedCurrentCodeValues.templateCode !== normalizedInitialCodeValues.templateCode;

  const handleStartEditingCodes = () => {
    setCodeValues(getInitialCodeValues());
    setCodeError(null);
    setIsEditingCodes(true);
  };

  const handleCancelEditingCodes = () => {
    setCodeValues(getInitialCodeValues());
    setCodeError(null);
    setIsEditingCodes(false);
  };

  const handleCodeValueChange = (field: keyof UpdateTokenCodesRequest, value: string) => {
    setCodeValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCodeError(null);
  };

  const handleNormalizeField = (field: keyof UpdateTokenCodesRequest) => {
    setCodeValues((prev) => ({
      ...prev,
      [field]: normalizeCodeInput(prev[field]),
    }));
  };

  const handleSaveCodes = async () => {
    const normalizedValues = normalizeDraftValues(codeValues);
    setCodeValues(normalizedValues);

    if (!hasCodeChanges) {
      return;
    }

    if (!normalizedValues.tokenCode || !normalizedValues.providerCode || !normalizedValues.templateCode) {
      setCodeError('All three codes are required.');
      return;
    }

    try {
      setCodeError(null);
      await updateTokenCodes.mutateAsync(normalizedValues);
      setIsEditingCodes(false);
    } catch (error) {
      setCodeError((error as ApiError).message || 'Failed to update codes.');
    }
  };

  const renderCodeRow = (label: string, value: string | undefined, field: string) => (
    <div className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2">
      <span className="text-gray-600">{label}</span>
      <div className="flex items-center space-x-2">
        <code className="font-mono text-xs text-gray-900">
          {value || '-'}
        </code>
        <button
          onClick={() => value && copyToClipboard(value, field)}
          disabled={!value}
          className="btn btn-outline px-2 py-1 text-xs flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {copiedField === field ? (
            <>
              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderCodeInputRow = (
    label: string,
    field: keyof UpdateTokenCodesRequest,
    placeholder: string
  ) => (
    <label className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2 gap-4">
      <span className="text-gray-600">{label}</span>
      <input
        type="text"
        value={codeValues[field]}
        placeholder={placeholder}
        onChange={(event) => handleCodeValueChange(field, event.target.value)}
        onBlur={() => handleNormalizeField(field)}
        className="w-64 max-w-full rounded border border-gray-300 px-2 py-1 font-mono text-xs text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
    </label>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Token Details</h2>
            <p className="text-sm text-gray-500 mt-1">
              View and copy access and refresh tokens
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading token details...</p>
            </div>
          ) : tokenDetails ? (
            <>
	              {/* Provider Info */}
	              <div className="bg-gray-50 rounded-lg p-4">
	                <h3 className="font-semibold text-gray-900 mb-2">Provider Information</h3>
	                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Provider:</span>{' '}
                    <span className="font-medium">{tokenDetails.providerName}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span>{' '}
                    <span className="font-medium">{tokenDetails.providerType}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>{' '}
                    <span className="font-medium">{tokenDetails.status}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Scope:</span>{' '}
                    <span className="font-medium">{displayScope}</span>
	                  </div>
	                </div>
	                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-100 p-3">
	                  <div className="mb-3 flex items-start justify-between gap-4">
	                    <div>
	                      <h4 className="font-medium text-gray-900">Codes</h4>
	                      <p className="text-xs text-gray-500">
	                        {isEditingCodes
	                          ? 'All three codes must be filled. Codes are normalized automatically.'
	                          : 'Copy or edit the token lookup codes.'}
	                      </p>
	                    </div>
	                    {!isEditingCodes && (
	                      <button
	                        onClick={handleStartEditingCodes}
	                        disabled={!token || updateTokenCodes.isPending}
	                        className="btn btn-outline px-3 py-1 text-sm"
	                      >
	                        Edit Codes
	                      </button>
	                    )}
	                  </div>
	                  <div className="grid grid-cols-1 gap-2 text-sm">
	                    {isEditingCodes ? (
	                      <>
	                        {renderCodeInputRow('Token Code', 'tokenCode', 'Enter token code')}
	                        {renderCodeInputRow('Provider Code', 'providerCode', 'Enter provider code')}
	                        {renderCodeInputRow('Template Code', 'templateCode', 'Enter template code')}
	                      </>
	                    ) : (
	                      <>
	                        {renderCodeRow('Token Code', token?.tokenCode, 'token-code')}
	                        {renderCodeRow('Provider Code', token?.providerCode, 'provider-code')}
	                        {renderCodeRow('Template Code', token?.providerTemplateCode, 'template-code')}
	                      </>
	                    )}
	                  </div>
	                  {isEditingCodes && (
	                    <div className="mt-3 space-y-3">
	                      {codeError && (
	                        <p className="text-sm text-red-600">{codeError}</p>
	                      )}
	                      <div className="flex justify-end gap-2">
	                        <button
	                          onClick={handleCancelEditingCodes}
	                          disabled={updateTokenCodes.isPending}
	                          className="btn btn-outline px-4 py-2 text-sm"
	                        >
	                          Cancel
	                        </button>
                        <button
                          onClick={handleSaveCodes}
                          disabled={updateTokenCodes.isPending || !hasCodeChanges}
                          className="btn btn-primary px-4 py-2 text-sm"
                        >
                          {updateTokenCodes.isPending ? 'Saving...' : 'Save'}
                        </button>
	                      </div>
	                    </div>
	                  )}
	                </div>
	              </div>

              {/* Access Token */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">Access Token</h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowAccessToken(!showAccessToken)}
                      className="btn btn-outline px-3 py-1 text-sm flex items-center space-x-1"
                    >
                      {showAccessToken ? (
                        <>
                          <EyeOff className="h-4 w-4" />
                          <span>Hide</span>
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4" />
                          <span>Show</span>
                        </>
                      )}
                    </button>
                    {showAccessToken && tokenDetails.accessToken && (
                      <button
                        onClick={() => copyToClipboard(tokenDetails.accessToken, 'access')}
                        className="btn btn-outline px-3 py-1 text-sm flex items-center space-x-1"
                      >
                        {copiedField === 'access' ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 rounded p-3 font-mono text-sm break-all">
                  {showAccessToken ? tokenDetails.accessToken : maskToken(tokenDetails.accessToken)}
                </div>
              </div>

              {/* Refresh Token */}
              {tokenDetails.refreshToken && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Refresh Token</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowRefreshToken(!showRefreshToken)}
                        className="btn btn-outline px-3 py-1 text-sm flex items-center space-x-1"
                      >
                        {showRefreshToken ? (
                          <>
                            <EyeOff className="h-4 w-4" />
                            <span>Hide</span>
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4" />
                            <span>Show</span>
                          </>
                        )}
                      </button>
                      {showRefreshToken && tokenDetails.refreshToken && (
                        <button
                          onClick={() => copyToClipboard(tokenDetails.refreshToken!, 'refresh')}
                          className="btn btn-outline px-3 py-1 text-sm flex items-center space-x-1"
                        >
                          {copiedField === 'refresh' ? (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded p-3 font-mono text-sm break-all">
                    {showRefreshToken ? tokenDetails.refreshToken : maskToken(tokenDetails.refreshToken)}
                  </div>
                </div>
              )}

              {/* Decoded JWT */}
              {decodedAccessToken && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Decoded JWT Payload</h3>
                    <button
                      onClick={() => setShowDecodedJWT(!showDecodedJWT)}
                      className="btn btn-outline px-3 py-1 text-sm flex items-center space-x-1"
                    >
                      {showDecodedJWT ? (
                        <>
                          <EyeOff className="h-4 w-4" />
                          <span>Hide</span>
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4" />
                          <span>Show</span>
                        </>
                      )}
                    </button>
                  </div>
                  {showDecodedJWT && (
                    <pre className="bg-gray-50 rounded p-3 font-mono text-xs overflow-x-auto">
                      {JSON.stringify(decodedAccessToken, null, 2)}
                    </pre>
                  )}
                </div>
              )}

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <span className="font-semibold">Security Warning:</span> These tokens provide
                  access to your external accounts. Do not share them with anyone or store them
                  insecurely.
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No token details available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button onClick={onClose} className="btn btn-primary px-6 py-2">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
